import React, { FC, useEffect, useMemo, useState, useTransition } from "react";
import clsx from "clsx";
import {
  Box,
  Grid2,
  Typography,
  Card,
  Portal,
  styled,
  useTheme,
  Divider,
  useMediaQuery,
  Button,
  Collapse,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import omit from "lodash.omit";

import { colorSystem } from "../../theme";

import dayjs, { Dayjs } from "dayjs";
import { NutritionTargetDto } from "@growth-machine-llc/stridist-api-client";
import {
  useQuery,
  keepPreviousData,
  useQueryClient,
  useMutation,
} from "@tanstack/react-query";
import {
  CLIENT_NUTRITION_ENTRIES_QUERY_KEY,
  CLIENT_NUTRITION_TARGET_RECOMENDATIONS_QUERY_KEY,
  CLIENT_NUTRITION_TARGETS_QUERY_KEY,
  CLIENT_NUTRITION_HIDDEN_TARGETS_QUERY_KEY,
  TimeUnit,
} from "../client-nutrition-targets/ClientNutritionCard";
import NutritionEntriesService from "../../services/NutritionEntriesService";
import { useClient } from "../../hooks/useClient";
import { ClientNutritionTargetList } from "../client-nutrition-targets/ClientNutritionTargetList";
import { ClientNutritionTargetEmpty } from "../client-nutrition-targets/ClientNutritionTargetEmpty";
import { NewDrawer } from "../client-nutrition-targets/targets/NewDrawer";
import { SkeletonCard } from "../card/SkeletonCard";
import { DateCalendar, PickersDay, PickersDayProps } from "@mui/x-date-pickers";
import ClientNutritionCardBarChart from "../client-nutrition-targets/ClientNutritionCardBarChart";
import { TextButton } from "../button/TextButton";
import NutritionTargetsService from "../../services/NutritionTargetsService";
import { chunkBy, interpolateNutritionData } from "../../utils/chart";
import { PickersMonth } from "@mui/x-date-pickers/MonthCalendar/PickersMonth";
import { ClientNutritionEntriesList } from "../client-nutrition-targets/ClientNutritionEntriesList";
import { NewEntriesDrawer } from "../client-nutrition-targets/entries/NewEntriesDrawer";
import { DeleteConfirmationProvider } from "../program/modals/DeleteConfirmation.tsx/DeleteConfirmationModalContext";
import updateLocale from "dayjs/plugin/updateLocale";
import ClientNutritionListSkeleton from "../client-nutrition-targets/ClientNutritionListSkeleton";
import {
  predicateQueryKeyByParams,
  useOptimisticUpdateMutation,
} from "../../hooks/useOptimisticUpdateMutation";

dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
  weekStart: 1,
});

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxWidth: theme.breakpoints.values.slg,
  },
  dateButton: {
    padding: theme.spacing(0, 1),
    fontSize: "unset",
    fontWeight: "unset",
    textDecoration: "underline",
    textDecorationColor: theme.palette.primary.main,
  },
  selectLabel: {
    fontSize: "1rem",
    fontWeight: "bold",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  card: {
    padding: theme.spacing(3),
  },

  titleContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 600,
    lineHeight: "29px",
    color: theme.palette.common.black,
    marginBottom: 4,
  },
  editTargets: {
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: "20px",
    color: theme.palette.primary.main,
    cursor: "pointer",
  },

  viewNav: {
    display: "flex",
    gap: theme.spacing(0.5),
    marginLeft: "auto",
    marginRight: "auto",

    [theme.breakpoints.between("sm", "md")]: {
      marginRight: 0,
    },

    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(2),
    },
  },

  button: {
    backgroundColor: "transparent",
    borderWidth: 0,
    color: theme.palette.text.secondary,
    borderRadius: 4,
    fontSize: 14,
    fontWeight: 700,
    margin: 0,
    padding: theme.spacing(0.8, 1),
    lineHeight: "17px",
    minWidth: 0,
    "&:hover": {
      color: colorSystem.white,
      backgroundColor: theme.palette.secondary.main,
    },
  },

  activeButton: {
    color: colorSystem.white,
    backgroundColor: theme.palette.secondary.main,
  },

  migrationMessage: {
    fontWeight: 500,
    color: theme.palette.grey[600],
  },

  migrationToggle: {
    cursor: "pointer",
    textDecoration: "underline",
  },
}));

interface CustomPickerDayProps extends PickersDayProps<Dayjs> {
  isSelected: boolean;
  isHovered: boolean;
  weekSelection: boolean;
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "isHovered",
})<CustomPickerDayProps>(
  ({ theme, isSelected, isHovered, weekSelection, day }) => ({
    width: theme.spacing(5),
    height: theme.spacing(5),
    borderRadius: weekSelection ? 0 : "50%",
    ...(isSelected && {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "&:hover, &:focus": {
        backgroundColor: theme.palette.primary.main,
      },
    }),
    ...(isHovered && {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "&:hover, &:focus": {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
      },
    }),
    ...(weekSelection &&
      day.isoWeekday() === 1 && {
        borderTopLeftRadius: "50%",
        borderBottomLeftRadius: "50%",
      }),
    ...(weekSelection &&
      day.isoWeekday() === 7 && {
        borderTopRightRadius: "50%",
        borderBottomRightRadius: "50%",
      }),
  }),
) as React.ComponentType<CustomPickerDayProps>;

const isInSameWeek = (dayA: Dayjs, dayB: Dayjs | null | undefined) => {
  if (dayB == null) {
    return false;
  }

  return dayA.isSame(dayB, "isoWeek");
};

function Day(
  props: PickersDayProps<Dayjs> & {
    selectedDay?: Dayjs | null;
    hoveredDay?: Dayjs | null;
    hasEntries?: boolean;
    weekSelection?: boolean;
  },
) {
  const { day, selectedDay, hoveredDay, weekSelection, hasEntries, ...other } =
    props;

  return (
    <CustomPickersDay
      {...other}
      day={day}
      disableMargin
      disableHighlightToday
      selected={false}
      children={
        <Box
          sx={
            day.isSame(dayjs(), "day") && {
              borderWidth: 2,
              borderRadius: "50%",
              paddingTop: 0.6,
              height: 40,
              width: 40,
              aspectRatio: "1 / 1",
            }
          }
        >
          <Typography sx={{ mb: 1 }} variant="body2">
            {day.date()}
          </Typography>
          {hasEntries && (
            <Typography
              sx={{ position: "absolute", bottom: -3, left: 16 }}
              variant="h6"
            >
              •
            </Typography>
          )}
        </Box>
      }
      isSelected={
        weekSelection
          ? isInSameWeek(day, selectedDay)
          : day.isSame(selectedDay, "day")
      }
      isHovered={
        weekSelection
          ? isInSameWeek(day, hoveredDay)
          : day.isSame(hoveredDay, "day")
      }
      weekSelection={weekSelection}
    />
  );
}

export interface ClientNutritionScreenProps {
  autoNutritionMacroTarget: NutritionTargetDto[];
  autoNutritionLastSync: string;
  loading: boolean;
}

const ClientNutritionScreen: FC<ClientNutritionScreenProps> = (props) => {
  const { autoNutritionMacroTarget, autoNutritionLastSync, loading } = props;

  const [activeButton, setActiveButton] = useState<TimeUnit>(TimeUnit.WEEK);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [hoveredDay, setHoveredDay] = useState<Dayjs | null>(null);

  const [targetsDrawerOpen, setTargetsDrawerOpen] = useState(false);
  const [entriesDrawerOpen, setEntriesDrawerOpen] = useState(false);

  const [migrationHiddenShown, setMigrationHiddenShown] = useState(false);

  const theme = useTheme();

  const activeTargets = React.useMemo(
    () => autoNutritionMacroTarget?.filter((t) => !t.expiredAt),
    [autoNutritionMacroTarget],
  );

  const { id: clientId, username } = useClient();

  const { data: nutritionEntries, isLoading: isLoadingEntries } = useQuery({
    queryKey: [CLIENT_NUTRITION_ENTRIES_QUERY_KEY, { username }],
    queryFn: () => NutritionEntriesService.getNutritionEntries(username),
    placeholderData: keepPreviousData,
  });

  const { data: nutritionTargets, isLoading: isLoadingTargets } = useQuery({
    queryKey: [CLIENT_NUTRITION_TARGETS_QUERY_KEY, { username }],
    queryFn: () => NutritionTargetsService.getNutritionTargets(username),
    placeholderData: keepPreviousData,
  });

  const { data: hiddenTargets, isLoading: isLoadingHiddenTargets } = useQuery({
    queryKey: [CLIENT_NUTRITION_HIDDEN_TARGETS_QUERY_KEY, { username }],
    queryFn: () => NutritionTargetsService.getHiddenTargets(username),
  });

  const queryClient = useQueryClient();

  const { mutate: deleteHiddenTargetMutation } = useOptimisticUpdateMutation({
    disableToastAlerts: true,
    queryKey: [CLIENT_NUTRITION_HIDDEN_TARGETS_QUERY_KEY, { username }],
    mutationFn: NutritionTargetsService.deleteHiddenTarget,
    optimisticUpdater: {
      updateFn: (_, variables) => {
        queryClient.setQueryData(
          [CLIENT_NUTRITION_HIDDEN_TARGETS_QUERY_KEY, { username }],
          (oldData: NutritionTargetDto[] | undefined) => {
            return oldData.filter((target) => target.id !== variables);
          },
        );
      },
    },
  });

  const sortedNutritionEntries = useMemo(
    () => nutritionEntries?.toSorted((a, b) => a.date.diff(b.date)),
    [nutritionEntries],
  );

  const entriesStartDate =
    sortedNutritionEntries?.[0]?.date?.startOf("day") || dayjs().startOf("day");
  const entriesEndDate =
    sortedNutritionEntries?.[sortedNutritionEntries.length - 1]?.date?.endOf(
      "day",
    ) || dayjs().endOf("day");

  const { data: targetRecomendations, isLoading: isLoadingRecomendations } =
    useQuery({
      queryKey: [
        CLIENT_NUTRITION_TARGET_RECOMENDATIONS_QUERY_KEY,
        { username, entriesStartDate, entriesEndDate },
      ],
      queryFn: () =>
        NutritionTargetsService.getRecomendedTargetList(
          username,
          entriesStartDate.year(),
          entriesStartDate.month() + 1,
          entriesStartDate.date(),
          entriesEndDate.year(),
          entriesEndDate.month() + 1,
          entriesEndDate.date(),
        ),
      enabled: Boolean(nutritionEntries?.length),
      placeholderData: keepPreviousData,
    });

  useEffect(() => {
    setSelectedDate(nutritionEntries?.[0]?.date as Dayjs);
  }, [nutritionEntries]);

  const s = useStyles(props);
  const [date, setDate] = useState(() => {
    return dayjs(new Date()).startOf("day");
  });

  const [currentTargetId, setCurrentTargetId] = useState<number | null>(() =>
    autoNutritionMacroTarget?.length ? autoNutritionMacroTarget[0].id : null,
  );
  const currentTarget = useMemo(() => {
    return (
      autoNutritionMacroTarget?.find(({ id }) => id === currentTargetId) || null
    );
  }, [nutritionEntries, currentTargetId]);

  const [isPending, startTransition] = useTransition();

  const nutritionRecordHistory = nutritionEntries?.map((target) => ({
    date: target.date ? target.date.format("YYYY-MM-DD") : null,
  }));

  const nutritionsOfSpecificDate = useMemo(() => {
    return nutritionEntries?.filter(
      (target) => target.date && target.date.isSame(date, "day"),
    );
  }, [nutritionEntries, date]);

  const nutritionRecord =
    nutritionsOfSpecificDate && nutritionsOfSpecificDate.length > 0
      ? nutritionsOfSpecificDate.reduce(
          (totals, entry) => {
            return Object.entries(entry).reduce((result, [key, value]) => {
              if (key in result) {
                return {
                  ...result,
                  [key]: result[key] + (+value || 0),
                };
              }
              return result;
            }, totals);
          },
          {
            calories: 0,
            carbs: 0,
            fat: 0,
            protein: 0,
            sugar: 0,
            fiber: 0,
            sodium: 0,
            alcohol: 0,
          },
        )
      : null;

  const alltimeAveragingPeriod = useMemo(() => {
    if (!nutritionEntries || !nutritionEntries.length) return "day";
    const firstEntry = nutritionEntries[0];
    const lastEntry = nutritionEntries[nutritionEntries.length - 1];
    const startDate = firstEntry?.date;
    const endDate = lastEntry?.date;
    const duration = startDate.diff(endDate, "weeks");
    if (duration <= 4) {
      return "day";
    } else if (duration <= 16) {
      return "week";
    } else if (duration <= 208) {
      return "month";
    } else {
      return "year";
    }
  }, [nutritionEntries]);

  const interpolatedData = useMemo(() => {
    if (!nutritionEntries || !nutritionTargets || !targetRecomendations)
      return [];
    return interpolateNutritionData(
      sortedNutritionEntries,
      nutritionTargets,
      targetRecomendations,
      false,
      "day",
    );
  }, [nutritionEntries, nutritionTargets, targetRecomendations]);

  const chartData = useMemo(() => {
    let chunkedData = [];
    switch (activeButton) {
      case "day":
        chunkedData = chunkBy(
          interpolatedData,
          ["dayOfYear", "year"],
          "date",
          "calories",
          false,
        );
        break;
      case "week":
        chunkedData = chunkBy(
          interpolatedData,
          ["isoWeek", "year"],
          "date",
          "calories",
          true,
        );
        break;
      case "month":
        chunkedData = chunkBy(
          interpolatedData,
          ["month", "year"],
          "date",
          "calories",
          true,
        );
        break;
      case "alltime":
        chunkedData = [
          interpolateNutritionData(
            nutritionEntries?.toSorted((a, b) => a.date.diff(b.date)) || [],
            nutritionTargets || [],
            targetRecomendations || [],
            true,
            alltimeAveragingPeriod,
            false,
            true,
          ),
        ];
        break;
      default:
        chunkedData = [[]];
    }

    chunkedData = chunkedData.filter((data) =>
      data.some(
        ({ calories, protein, fat, carbs }) =>
          !(
            calories === undefined &&
            protein === undefined &&
            fat === undefined &&
            carbs === undefined
          ),
      ),
    );

    return chunkedData;
  }, [interpolatedData, activeButton]);

  const handleToggleShowMigrationHidden = () => {
    setMigrationHiddenShown((prev) => !prev);
  };

  const displayData =
    activeButton === "alltime"
      ? chartData[0]
      : chartData.find((item) =>
          selectedDate?.isSame(item[0].date, activeButton as any),
        );

  return (
    <DeleteConfirmationProvider>
      <Box
        sx={{
          paddingX: { xs: "0.5rem", sm: "2rem" },
          paddingBottom: { xs: "0.5rem", sm: "2rem" },
          display: "flex",
          flexDirection: "column",
          gap: "2rem",
          maxWidth: theme.breakpoints.values.slg,
          marginX: "auto",
        }}
      >
        <>
          {!isLoadingTargets && activeTargets && (
            <NewDrawer
              open={targetsDrawerOpen}
              onClose={() => setTargetsDrawerOpen(false)}
              nutritionTargets={activeTargets}
              clientInfo={{ clientId, username }}
            />
          )}
          <Card className={s.card}>
            <Box className={s.titleContainer}>
              <Typography variant="h6" className={s.title}>
                Nutrition targets
              </Typography>
              <Button
                variant="text"
                className={s.editTargets}
                onClick={() => setTargetsDrawerOpen((open) => !open)}
                disabled={isLoadingTargets}
              >
                Edit
              </Button>
            </Box>
            {!loading ? (
              activeTargets?.length ? (
                <>
                  <ClientNutritionTargetList
                    title="Macro targets"
                    targets={activeTargets}
                    isProgram={false}
                    handleAddNutrition={() => {}}
                  />
                  {hiddenTargets?.length ? (
                    <>
                      <Box className={s.migrationMessage}>
                        <Typography variant="caption">
                          The client has older targets that are no longer
                          visible and will be removed completely on July 1st.{" "}
                          <span
                            onClick={handleToggleShowMigrationHidden}
                            className={s.migrationToggle}
                          >
                            {migrationHiddenShown
                              ? "Hide targets"
                              : "See targets"}
                          </span>
                        </Typography>
                      </Box>
                      <Collapse
                        sx={{ mt: 2 }}
                        in={migrationHiddenShown}
                        timeout="auto"
                        unmountOnExit
                      >
                        <ClientNutritionTargetList
                          migrationHidden
                          onDeleteMigrationHiddenTarget={
                            deleteHiddenTargetMutation
                          }
                          title="Hidden targets"
                          targets={hiddenTargets}
                          isProgram={false}
                          handleAddNutrition={() => {}}
                        />
                      </Collapse>
                    </>
                  ) : null}
                </>
              ) : (
                <ClientNutritionTargetEmpty
                  type="target"
                  onClick={() => setTargetsDrawerOpen(true)}
                />
              )
            ) : (
              <ClientNutritionListSkeleton />
            )}
          </Card>
        </>
        <Card className={s.card}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              width: "100%",
            }}
          >
            <Typography
              sx={{ width: 0, textWrap: "nowrap" }}
              variant="h6"
              className={s.title}
            >
              Nutrition targets
            </Typography>
            <Box className={s.viewNav}>
              <TextButton
                className={clsx(
                  s.button,
                  activeButton === "day" && s.activeButton,
                )}
                onClick={() => {
                  setActiveButton(TimeUnit.DAY);
                }}
              >
                Day
              </TextButton>
              <TextButton
                className={clsx(
                  s.button,
                  activeButton === "week" && s.activeButton,
                )}
                onClick={() => {
                  setActiveButton(TimeUnit.WEEK);
                }}
              >
                Week
              </TextButton>
              <TextButton
                className={clsx(
                  s.button,
                  activeButton === "month" && s.activeButton,
                )}
                onClick={() => {
                  setActiveButton(TimeUnit.MONTH);
                }}
              >
                Month
              </TextButton>
              <TextButton
                className={clsx(
                  s.button,
                  activeButton === "alltime" && s.activeButton,
                )}
                onClick={() => {
                  setActiveButton(TimeUnit.ALLTIME);
                }}
              >
                All time
              </TextButton>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              width: "100%",
              gap: { xs: 0, sm: 5 },
            }}
          >
            <Box sx={{ width: "min-content" }}>
              <DateCalendar
                sx={{
                  mx: 0,
                  width: "100%",
                }}
                showDaysOutsideCurrentMonth={activeButton === "week"}
                shouldDisableDate={(date) =>
                  !nutritionEntries?.find((day) =>
                    day.date.isSame(
                      date,
                      activeButton === "week" ? "week" : "day",
                    ),
                  )
                }
                shouldDisableMonth={(date) =>
                  activeButton === "alltime" ||
                  !nutritionEntries.find((day) =>
                    day.date.isSame(date, "month"),
                  )
                }
                slots={{ day: Day }}
                slotProps={{
                  day: (ownerState) =>
                    ({
                      selectedDay: activeButton !== "alltime" && selectedDate,
                      hoveredDay,
                      weekSelection: activeButton === "week",
                      hasEntries: Boolean(
                        nutritionEntries?.find((day) =>
                          day.date.isSame(ownerState.day, "day"),
                        ),
                      ),
                      onPointerEnter: () => setHoveredDay(ownerState.day),
                      onPointerLeave: () => setHoveredDay(null),
                    }) as any,
                }}
                views={[
                  (["week", "alltime"].includes(activeButton)
                    ? "day"
                    : activeButton) as any,
                ]}
                value={selectedDate}
                onChange={setSelectedDate}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <ClientNutritionCardBarChart data={displayData} />
            </Box>
          </Box>
        </Card>

        <Card className={s.card}>
          <Box className={s.titleContainer}>
            <Typography variant="h6" className={s.title}>
              Nutrition entries
            </Typography>
            <Button
              variant="text"
              className={s.editTargets}
              onClick={() => setEntriesDrawerOpen((open) => !open)}
              disabled={
                isLoadingEntries || isLoadingTargets || isLoadingRecomendations
              }
            >
              Edit
            </Button>
          </Box>
          {!isLoadingEntries &&
            !isLoadingTargets &&
            !isLoadingRecomendations && (
              <NewEntriesDrawer
                clientInfo={{ clientId, username }}
                nutritionTargets={nutritionTargets || []}
                entries={nutritionEntries || []}
                targetRecomendations={targetRecomendations || []}
                onClose={() => setEntriesDrawerOpen(false)}
                open={entriesDrawerOpen}
                isCoachView={true}
              />
            )}
          {!loading ? (
            displayData?.length ? (
              <ClientNutritionEntriesList
                title="Macro targets"
                entries={
                  activeButton === "alltime"
                    ? nutritionEntries
                    : nutritionEntries.filter((item) => {
                        const itemDate = item.date as Dayjs;
                        return selectedDate?.isSame(
                          itemDate,
                          activeButton as any,
                        );
                      })
                }
                isProgram={false}
                handleAddNutrition={() => {}}
              />
            ) : (
              <ClientNutritionTargetEmpty
                type="entry"
                onClick={() => setTargetsDrawerOpen(true)}
              />
            )
          ) : (
            <ClientNutritionListSkeleton />
          )}
        </Card>
      </Box>
    </DeleteConfirmationProvider>
  );
};

export default ClientNutritionScreen;
